import { NgZone } from '@angular/core';
import { NodeHtmlMarkdown, PostProcessResult } from './html-to-markdown';

export async function preloadImage(url: string) {
    if (url.startsWith('data:'))
        return;

    await new Promise<void>((resolve, reject) => {
        let img = document.createElement('img');
        img.src = url;
        img.style.display = 'none';
        img.addEventListener('load', () => (img.remove(), resolve()));
        img.addEventListener('error', () => reject(new Error(`Failed to load image ${url}`)));
        document.body.appendChild(img);
    });
}

/**
 * Surround tag content with delimiter (moving any leading/trailing space to outside the tag
 */
export function tagSurround(content: string, surroundStr: string) {
    // If un-escaped surroundStr already occurs, remove all instances
    // See: https://github.com/crosstype/node-html-markdown/issues/18
    const nestedSurroundStrIndex = content.indexOf(surroundStr);
    if (nestedSurroundStrIndex >= 0)
        content = content.replace(
            new RegExp(`([^\\\\])\\${surroundStr.split('').join('\\')}`, 'gm'),
            '$1'
        );

    const lines = splitSpecial(content);
    let res = '';

    for (const { text, newLineChar } of lines) {
        let i: number = 0;
        let startPos: number | undefined = undefined;
        let endPos: number | undefined = undefined;

        while (i >= 0 && i < text.length) {
            if (/[\S]/.test(text[i])) {
                if (startPos === undefined) {
                    startPos = i;
                    i = text.length;
                } else {
                    endPos = i;
                    i = NaN;
                }
            }

            if (startPos === undefined) ++i;
            else --i;
        }

        // If whole string is non-breaking whitespace, don't surround it
        if (startPos === undefined) {
            res += text + newLineChar;
            continue;
        }

        if (endPos === undefined) endPos = text.length - 1;

        const leadingSpace = startPos > 0 ? text[startPos - 1] : '';
        const trailingSpace = endPos < (text.length - 1) ? text[endPos + 1] : '';

        const slicedText = text.slice(startPos, endPos + 1);

        res += leadingSpace + surroundStr + slicedText + surroundStr + trailingSpace + newLineChar;
    }

    return res;

    /**
     * Split string, preserving specific newline used for each line
     */
    function splitSpecial(s: string) {
        const lines: { text: string, newLineChar: '\r' | '\n' | '\r\n' | ''; }[] = [];
        const strLen = s.length;

        for (let i = 0, startPos = 0; i < strLen; ++i) {
            let char = s.charAt(i);
            let newLineChar: typeof lines[number]['newLineChar'] = '';

            if (char === '\r') newLineChar = (s.charAt(i + 1) === '\n') ? '\r\n' : char;
            else if (char === '\n') newLineChar = char;

            const endPos = newLineChar ? i :
                i === (strLen - 1) ? i + 1 :
                    undefined;

            if (endPos === undefined) continue;

            lines.push({
                text: s.slice(startPos, endPos),
                newLineChar
            });

            startPos = endPos + newLineChar.length;
            if (newLineChar.length > 1) ++i;
        }

        return lines;
    }

}

export function htmlToMarkdown(html: string) {

    let doc = document.implementation.createHTMLDocument();
    doc.write(html);

    let changed = true;
    while (changed) {
        changed = false;

        // Google Docs can generate <b style="font-weight: normal">, that should not be considered "bold" (**)
        for (let el of Array.from(doc.querySelectorAll('b'))) {
            if (el && el.style.fontWeight === 'normal') {
                let span = doc.createElement('span');
                el.getAttributeNames().forEach(attr => span.setAttribute(attr, el.getAttribute(attr)));
                span.innerHTML = el.innerHTML;
                el.replaceWith(span);
                changed = true;
            }
        }

        // Detect a bold span and separate it out into a <b> tag (again, Google Docs)
        for (let el of Array.from(doc.querySelectorAll('span'))) {
            if (el && (Number(el.style.fontWeight) >= 700 || ['bold', 'bolder'].includes(el.style.fontWeight))) {
                el.style.fontWeight = '';

                let b = doc.createElement('b');
                el.replaceWith(b);
                b.appendChild(el);
                changed = true;
            }
        }

        // Detect an italic span and separate it out into an <i> tag (again, Google Docs)
        for (let el of Array.from(doc.querySelectorAll('span'))) {
            if (['italic', 'oblique'].includes(el.style.fontStyle)) {
                el.style.fontStyle = '';

                let em = doc.createElement('em');
                el.replaceWith(em);
                em.appendChild(el);
                changed = true;
            }
        }

        // Detect an underlined span and separate it out into an <u> tag (again, Google Docs)
        for (let el of Array.from(doc.querySelectorAll('span'))) {
            if (['underline'].includes(el.style.textDecoration)) {
                el.style.textDecoration = '';

                let em = doc.createElement('u');
                el.replaceWith(em);
                em.appendChild(el);
                changed = true;
            }
        }
    }

    let originalHtml = html;
    html = doc.documentElement.outerHTML;

    let markdown = NodeHtmlMarkdown.translate(html, {}, {
        'u': {
            spaceIfRepeatingChar: true,
            postprocess: ({ content, options: { strongDelimiter } }) =>
                content.replace(/\s+/g, '') === ''
                    ? PostProcessResult.RemoveNode
                    : tagSurround(content, '++')
        }
    });

    return markdown;
}

export async function loadScript(url: string, options: { async?: boolean } = {}) {
    let script = document.createElement('script');

    script.src = url;
    script.async = options.async ?? true;

    document.head.append(script);

    await new Promise<void>((resolve, reject) => {
        script.addEventListener('load', () => resolve());
        script.addEventListener('error', () => reject(`Error occurred while loading script ${url}`));
    });
}

export function getStyleFromUrl(url: string, fallbackUrl?: string) {
    return fallbackUrl ? `url(${url}), url(${fallbackUrl})` : `url(${url})`;
}

export function isElementVisible(element: HTMLElement) {
    let rect = element.getBoundingClientRect();
    let verticallyAligned = (rect.top >= 0 && rect.top <= window.innerHeight) || (rect.bottom >= 0 && rect.bottom <= window.innerHeight);
    let horizontallyAligned = (rect.right >= 0 && rect.right <= window.innerWidth) || (rect.left >= 0 && rect.left <= window.innerWidth);

    if (rect.width == 0 && rect.height == 0)
        verticallyAligned = horizontallyAligned = false;

    return verticallyAligned && horizontallyAligned;
}

/**
 * Resolves when the given element becomes visible to the user.
 * - If the element is already visible, it resolves immediately
 * - If the user agent does not support IntersectionObserver, it resolves immediately
 *
 * @param ngZone
 * @param element
 * @returns
 */
export async function waitUntilVisible(ngZone: NgZone, element: HTMLElement): Promise<void> {
    return new Promise<void>(resolve => {
        setTimeout(() => {
            if (isElementVisible(element) || typeof IntersectionObserver === 'undefined') {
                resolve();
                return;
            }

            ngZone.runOutsideAngular(() => {
                let observer: IntersectionObserver;
                observer = new IntersectionObserver(async ([ entry ]) => {
                    if (entry?.isIntersecting) {
                        observer.unobserve(element);
                        ngZone.runGuarded(() => setTimeout(async () => resolve()));
                    }
                }, {
                    rootMargin: '1000px',
                    threshold: 0
                });
                observer.observe(element);
            });
        });
    });
}

export function isNewTabClick(event: MouseEvent) {
    return event.ctrlKey || event.shiftKey || event.altKey || event.metaKey;
}